import * as React from "react";
import { cn } from "@/lib/utils";
import { GradientText, SectionLabel } from "./GradientText";
import { ChevronLeft, ChevronRight } from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";

interface Screenshot {
  id: string;
  image: string;
  caption: string;
}

interface AppScreenshotsProps {
  title: string;
  subtitle?: string;
  items: Screenshot[];
}

export function AppScreenshots({ title, subtitle, items }: AppScreenshotsProps) {
  const [isVisible, setIsVisible] = React.useState(false);
  const sectionRef = React.useRef<HTMLDivElement>(null);
  const [selectedIndex, setSelectedIndex] = React.useState(0);

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });

  // Autoplay
  React.useEffect(() => {
    if (!emblaApi) return;

    const interval = setInterval(() => {
      emblaApi.scrollNext();
    }, 4000);

    return () => clearInterval(interval);
  }, [emblaApi]);

  // Track selected slide
  React.useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      setSelectedIndex(emblaApi.selectedScrollSnap());
    };

    emblaApi.on("select", onSelect);
    onSelect();

    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi]);

  // Intersection observer for scroll animations
  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const scrollPrev = React.useCallback(() => {
    emblaApi?.scrollPrev();
  }, [emblaApi]);

  const scrollNext = React.useCallback(() => {
    emblaApi?.scrollNext();
  }, [emblaApi]);

  const scrollTo = React.useCallback(
    (index: number) => {
      emblaApi?.scrollTo(index);
    },
    [emblaApi]
  );

  return (
    <section ref={sectionRef} className="py-24 lg:py-32">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div
          className={cn(
            "text-center max-w-3xl mx-auto mb-16 lg:mb-20",
            "transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]",
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          )}
        >
          <SectionLabel className="mb-6">App Preview</SectionLabel>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mb-6">
            <GradientText>{title}</GradientText>
          </h2>

          {subtitle && (
            <p className="text-muted-foreground text-base lg:text-lg leading-relaxed">
              {subtitle}
            </p>
          )}
        </div>

        {/* Carousel */}
        <div
          className={cn(
            "relative max-w-sm mx-auto",
            "transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]",
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          )}
          style={{ transitionDelay: isVisible ? "200ms" : "0ms" }}
        >
          {/* Phone mockup frame */}
          <div
            className={cn(
              "relative rounded-[2.5rem] overflow-hidden",
              "bg-muted",
              "border-[8px] border-border",
              "shadow-xl"
            )}
          >
            {/* Notch */}
            <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-6 bg-black rounded-full z-10" />

            {/* Carousel container */}
            <div className="overflow-hidden" ref={emblaRef}>
              <div className="flex">
                {items.map((screenshot) => (
                  <div key={screenshot.id} className="flex-[0_0_100%] min-w-0">
                    <div className="aspect-[9/19] bg-muted flex items-center justify-center">
                      <img
                        src={screenshot.image}
                        alt={screenshot.caption}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                          e.currentTarget.parentElement!.innerHTML = `
                            <div class="flex items-center justify-center h-full text-muted-foreground text-sm p-4 text-center">
                              ${screenshot.caption}
                            </div>
                          `;
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Navigation arrows */}
          <button
            onClick={scrollPrev}
            className={cn(
              "absolute left-0 top-1/2 -translate-y-1/2 -translate-x-12 lg:-translate-x-16",
              "w-10 h-10 rounded-full flex items-center justify-center",
              "bg-muted border border-border",
              "text-foreground hover:bg-muted/80 transition-colors"
            )}
            aria-label="Previous screenshot"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          <button
            onClick={scrollNext}
            className={cn(
              "absolute right-0 top-1/2 -translate-y-1/2 translate-x-12 lg:translate-x-16",
              "w-10 h-10 rounded-full flex items-center justify-center",
              "bg-muted border border-border",
              "text-foreground hover:bg-muted/80 transition-colors"
            )}
            aria-label="Next screenshot"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Caption */}
        <p
          className={cn(
            "text-center text-muted-foreground mt-6 text-sm lg:text-base",
            "transition-all duration-500",
            isVisible ? "opacity-100" : "opacity-0"
          )}
          style={{ transitionDelay: isVisible ? "400ms" : "0ms" }}
        >
          {items[selectedIndex]?.caption}
        </p>

        {/* Dot indicators */}
        <div
          className={cn(
            "flex justify-center gap-2 mt-6",
            "transition-all duration-500",
            isVisible ? "opacity-100" : "opacity-0"
          )}
          style={{ transitionDelay: isVisible ? "500ms" : "0ms" }}
        >
          {items.map((_, index) => (
            <button
              key={index}
              onClick={() => scrollTo(index)}
              className={cn(
                "w-2 h-2 rounded-full transition-all duration-300",
                index === selectedIndex
                  ? "bg-accent w-6"
                  : "bg-border hover:bg-muted-foreground/40"
              )}
              aria-label={`Go to screenshot ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
